/**
 * Drawer 'Novo/Editar recurso' (Dialog largo, padrão contract-drawer):
 * Nome* → Cliente* → Tipo* (atalho '+ novo tipo') → Grupo (filtrado pelo
 * cliente, atalho '+ grupo') → Serial → IMEI → datas → Ativo → Observações.
 */
import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import type { Equipment, EquipmentBody } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Input, Label, Select, Textarea } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Dialog } from '@/components/ui/misc';
import { ClientPicker, type ClientRef } from '@/features/tickets/ClientPicker';
import {
  equipmentGroupsOptions,
  equipmentTypesOptions,
  errorMessage,
} from './resources-queries';
import { useCreateEquipment, useCreateEquipmentGroup, useUpdateEquipment } from './resources-mutations';
import { ResourceTypesDialog } from './resource-types-dialog';

export function ResourceDrawer({
  initial,
  presetClient,
  onClose,
}: {
  initial?: Equipment;
  presetClient?: ClientRef;
  onClose: () => void;
}) {
  const [client, setClient] = useState<ClientRef | null>(
    // o veículo do SGR de ponto de venda sem cliente (spec 032) chega sem cliente
    initial
      ? initial.clientId !== null && initial.clientName !== null
        ? { id: initial.clientId, name: initial.clientName }
        : null
      : (presetClient ?? null)
  );
  const [name, setName] = useState(initial?.name ?? '');
  const [equipmentTypeId, setEquipmentTypeId] = useState(
    initial ? String(initial.equipmentTypeId) : ''
  );
  const [groupId, setGroupId] = useState(
    initial?.equipmentGroupId != null ? String(initial.equipmentGroupId) : ''
  );
  const [serialNumber, setSerialNumber] = useState(initial?.serialNumber ?? '');
  const [imei, setImei] = useState(initial?.imei ?? '');
  const [acquisitionDate, setAcquisitionDate] = useState(initial?.acquisitionDate ?? '');
  const [warrantyDate, setWarrantyDate] = useState(initial?.warrantyDate ?? '');
  const [active, setActive] = useState(initial?.active ?? true);
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [typesOpen, setTypesOpen] = useState(false);
  const [newGroupOpen, setNewGroupOpen] = useState(false);

  const typesQ = useQuery(equipmentTypesOptions());
  const allTypes = typesQ.data ?? [];
  // Ativos + o tipo do recurso em edição mesmo se inativado depois.
  const selectableTypes = allTypes.filter(
    (t) => t.active || (initial !== undefined && t.id === initial.equipmentTypeId)
  );

  const groupsQ = useQuery({
    ...equipmentGroupsOptions(client?.id),
    enabled: client !== null,
  });
  const groups = groupsQ.data ?? [];

  const create = useCreateEquipment();
  const update = useUpdateEquipment(initial?.id ?? 0);
  const mutation = initial ? update : create;

  const selectClient = (next: ClientRef | null) => {
    if (next?.id !== client?.id) setGroupId('');
    setClient(next);
  };

  // spec 032: o recurso que veio do SGR não muda placa, cliente nem tipo pela tela
  // (a API recusaria com 409) — notas, grupo, série, IMEI e garantia, sim
  const fromSgr = initial?.sgrId != null;
  const valid = (client !== null || fromSgr) && name.trim() !== '' && equipmentTypeId !== '';

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!valid || (client === null && !fromSgr)) return;
    const body: EquipmentBody = {
      name: name.trim(),
      clientId: fromSgr ? (initial?.clientId ?? null) : (client?.id ?? null),
      equipmentTypeId: Number(equipmentTypeId),
      equipmentGroupId: groupId !== '' ? Number(groupId) : null,
      serialNumber: serialNumber.trim() || null,
      imei: imei.trim() || null,
      acquisitionDate: acquisitionDate || null,
      warrantyDate: warrantyDate || null,
      active,
      notes: notes.trim() || null,
    };
    mutation.mutate(body, { onSuccess: onClose });
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={initial ? 'Editar recurso' : 'Novo recurso'}
      className="sm:max-w-2xl"
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="resource-name">
            Nome <span className="text-destructive">*</span>
          </Label>
          <Input
            id="resource-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex.: Notebook Dell Latitude"
            required
            disabled={fromSgr}
            autoFocus={!initial}
          />
          {fromSgr ? (
            <p className="text-xs text-muted-foreground">Vem do cadastro de origem: nome, cliente e tipo não mudam aqui.</p>
          ) : null}
        </div>

        <div className="space-y-1.5">
          <Label>
            Cliente <span className="text-destructive">*</span>
          </Label>
          {fromSgr ? (
            <p className="text-sm">{client?.name ?? '—'}</p>
          ) : (
            <ClientPicker selected={client} onSelect={selectClient} />
          )}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="resource-type">
                Tipo <span className="text-destructive">*</span>
              </Label>
              <button
                type="button"
                onClick={() => setTypesOpen(true)}
                className="flex items-center gap-1 text-[13px] font-medium text-primary transition-colors hover:text-primary-pressed"
              >
                <Plus className="size-3.5" />
                novo tipo
              </button>
            </div>
            <Select
              id="resource-type"
              value={equipmentTypeId}
              onChange={(e) => setEquipmentTypeId(e.target.value)}
              disabled={typesQ.isPending || fromSgr}
              required
            >
              <option value="" disabled>
                {typesQ.isPending ? 'Carregando tipos…' : 'Selecione o tipo…'}
              </option>
              {selectableTypes.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </Select>
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="resource-group">Grupo</Label>
              {client !== null ? (
                <button
                  type="button"
                  onClick={() => setNewGroupOpen(true)}
                  className="flex items-center gap-1 text-[13px] font-medium text-primary transition-colors hover:text-primary-pressed"
                >
                  <Plus className="size-3.5" />
                  grupo
                </button>
              ) : null}
            </div>
            <Select
              id="resource-group"
              value={groupId}
              onChange={(e) => setGroupId(e.target.value)}
              disabled={client === null || groupsQ.isPending}
            >
              <option value="">
                {client === null ? 'Selecione o cliente primeiro' : 'Sem grupo'}
              </option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </Select>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="resource-serial">Serial</Label>
            <Input
              id="resource-serial"
              value={serialNumber}
              onChange={(e) => setSerialNumber(e.target.value)}
              className="tabular-nums"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="resource-imei">IMEI</Label>
            <Input
              id="resource-imei"
              value={imei}
              onChange={(e) => setImei(e.target.value)}
              className="tabular-nums"
            />
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="resource-acquisition">Data de aquisição</Label>
            <Input
              id="resource-acquisition"
              type="date"
              value={acquisitionDate}
              onChange={(e) => setAcquisitionDate(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="resource-warranty">Garantia até</Label>
            <Input
              id="resource-warranty"
              type="date"
              value={warrantyDate}
              onChange={(e) => setWarrantyDate(e.target.value)}
            />
          </div>
        </div>

        <label className="flex w-fit cursor-pointer items-center gap-2 text-sm">
          <Switch checked={active} onCheckedChange={setActive} aria-label="Recurso ativo" />
          Recurso ativo
        </label>

        <div className="space-y-1.5">
          <Label htmlFor="resource-notes">Observações</Label>
          <Textarea id="resource-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>

        {mutation.error ? (
          <p className="text-sm text-destructive">{errorMessage(mutation.error)}</p>
        ) : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={mutation.isPending || !valid}>
            {mutation.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </form>

      {typesOpen ? <ResourceTypesDialog onClose={() => setTypesOpen(false)} /> : null}
      {newGroupOpen && client !== null ? (
        <NewGroupDialog
          clientId={client.id}
          clientName={client.name}
          onCreated={(id) => setGroupId(String(id))}
          onClose={() => setNewGroupOpen(false)}
        />
      ) : null}
    </Dialog>
  );
}

/** Dialog inline '+ grupo': Nome → POST /resources/groups com o cliente atual. */
function NewGroupDialog({
  clientId,
  clientName,
  onCreated,
  onClose,
}: {
  clientId: number;
  clientName: string;
  onCreated: (groupId: number) => void;
  onClose: () => void;
}) {
  const create = useCreateEquipmentGroup();
  const [name, setName] = useState('');

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    create.mutate(
      { clientId, name: name.trim() },
      {
        onSuccess: (group) => {
          onCreated(group.id);
          onClose();
        },
      }
    );
  };

  return (
    <Dialog open onClose={onClose} title="Novo grupo de recursos">
      <form onSubmit={submit} className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Grupo para o cliente <span className="font-medium text-foreground">{clientName}</span>.
        </p>
        <div className="space-y-1.5">
          <Label htmlFor="new-group-name">
            Nome <span className="text-destructive">*</span>
          </Label>
          <Input
            id="new-group-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex.: Matriz, Filial SP…"
            required
            autoFocus
          />
        </div>
        {create.error ? (
          <p className="text-sm text-destructive">{errorMessage(create.error)}</p>
        ) : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={create.isPending || !name.trim()}>
            {create.isPending ? 'Criando…' : 'Criar grupo'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
